import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useMatch } from 'react-router-dom';
import api from '../../utils/api';

const CITIES = ['Магнитогорск', 'Челябинск', 'Омск'];

const ROLES = [
  { value: 'retail_seller', label: 'Розничный продавец', group: 'retail' },
  { value: 'retail_admin', label: 'Администратор розницы', group: 'retail' },
  { value: 'admin_retail_hs', label: 'Администратор розницы HS', group: 'retail' },
  { value: 'admin_retail_ts', label: 'Администратор розницы TS', group: 'retail' },
  { value: 'retail_manager', label: 'Руководитель розницы', group: 'retail' },
  { value: 'marketer', label: 'Маркетолог', group: 'retail' },
  { value: 'wholesale', label: 'Оптовый отдел', group: 'other' },
  { value: 'office', label: 'Офисный сотрудник', group: 'other' },
  { value: 'warehouse', label: 'Сотрудник склада', group: 'other' },
  { value: 'company_head', label: 'Руководитель компании', group: 'head' },
];

const FIXED_NETWORK_ROLES = {
  admin_retail_hs: { network: 'hookah', label: 'Hookah-Service' },
  admin_retail_ts: { network: 'tabakov_mgn', label: 'Tabakov-Shop (Магнитогорск)' },
};

const DEFAULT_NETWORKS = {
  tabakov_mgn: {
    name: 'Tabakov-Shop (Магнитогорск)',
    points: ['ТРК Jazz Mall', 'ТРК Континент', 'пр. Карла Маркса, 61', 'ул. Октябрьская, 23', 'ул. Бориса Ручьева, 192а', 'ул. Маяковская, 21', 'г. Белорецк, ул. Карла Маркса, 84'],
  },
  tabakov_chel: { name: 'Tabakov-Shop (Челябинск)', points: [] },
  hookah: {
    name: 'Hookah-Service',
    points: ['пр. Ленина, 47', 'ТРК «Гостиный двор»', 'ТРК «Континент»', 'ТРК «Семейный парк»', 'ТЦ «Тройка»', 'ТЦ «Казачий»', 'Белорецк, ул. Карла Маркса, 55'],
  },
};

const AUTO_ALL_ROLES = ['company_head', 'retail_manager', 'marketer'];
const NETWORK_ONLY_ROLES = ['retail_admin'];
const NETWORK_POINT_ROLES = ['retail_seller'];
const PLACE_ROLES = ['wholesale', 'office', 'warehouse'];

export default function UserApproval() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!useMatch('/admin/users/:id/edit');

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [city, setCity] = useState('');
  const [role, setRole] = useState('');
  const [network, setNetwork] = useState('');
  const [point, setPoint] = useState('');
  const [place, setPlace] = useState('');
  const [networks, setNetworks] = useState(DEFAULT_NETWORKS);

  useEffect(() => {
    api.get('/api/networks').then(res => {
      const payload = res.data.networks;
      if (Array.isArray(payload)) {
        const mapped = {};
        payload.filter(n => n.is_active !== false).forEach(n => {
          const zonePoints = (n.zones || []).flatMap(z => (z.points || []).map(p => p.name));
          const loosePoints = (n.points || []).map(p => p.name);
          mapped[n.slug] = { name: n.name, points: [...zonePoints, ...loosePoints] };
        });
        setNetworks(Object.keys(mapped).length ? mapped : DEFAULT_NETWORKS);
      } else if (payload && typeof payload === 'object') {
        setNetworks(payload);
      }
    }).catch(() => {});

    api.get('/api/admin/users').then(res => {
      const u = res.data.find(u => u.id === parseInt(id));
      if (u) {
        setUser(u);
        if (isEditMode) {
          setCity(u.city || u.cities_requested?.[0] || '');
          const r = u.role !== 'admin' ? u.role : '';
          setRole(r);
          setNetwork(u.network || '');
          if (PLACE_ROLES.includes(r) && u.primary_point) {
            setPlace(u.primary_point);
          } else if (NETWORK_POINT_ROLES.includes(r) && u.primary_point && u.primary_point !== 'all') {
            setPoint(u.primary_point);
          }
        } else {
          setCity(u.cities_requested?.[0] || '');
        }
      } else {
        setError('Пользователь не найден');
      }
    }).catch(() => setError('Ошибка загрузки')).finally(() => setLoading(false));
  }, [id, isEditMode]);

  const isAutoAll = AUTO_ALL_ROLES.includes(role);
  const isFixedNetwork = !!FIXED_NETWORK_ROLES[role];
  const isNetworkOnly = NETWORK_ONLY_ROLES.includes(role);
  const isNetworkAndPoint = NETWORK_POINT_ROLES.includes(role);
  const isPlace = PLACE_ROLES.includes(role);
  const isAllNetwork = network === 'all';
  const points = network && networks[network] ? networks[network].points : [];

  const canSubmit = city && role && (
    isAutoAll ||
    isFixedNetwork ||
    (isNetworkAndPoint && network && (isAllNetwork || point)) ||
    (isNetworkOnly && network) ||
    (isPlace && place)
  );

  const buildBody = () => {
    const fixedNet = FIXED_NETWORK_ROLES[role]?.network;
    return {
      city,
      role,
      network: isAutoAll ? 'all'
        : fixedNet ? fixedNet
        : (isNetworkOnly || isNetworkAndPoint) ? network
        : null,
      primary_point: isAutoAll ? 'all'
        : fixedNet ? 'all'
        : isNetworkOnly ? 'all'
        : isNetworkAndPoint ? (isAllNetwork ? 'all' : point)
        : place,
    };
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      if (isEditMode) {
        await api.put(`/api/admin/users/${id}`, buildBody());
      } else {
        await api.post(`/api/admin/users/${id}/approve`, buildBody());
      }
      navigate('/admin/users');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Отклонить пользователя?')) return;
    setSubmitting(true);
    try {
      await api.post(`/api/admin/users/${id}/reject`);
      navigate('/admin/users');
    } catch {
      setError('Ошибка отклонения');
      setSubmitting(false);
    }
  };

  if (loading) return <div className="page-loader">Загрузка...</div>;
  if (!user) return <div className="error-page"><div className="error-msg">{error}</div><button className="btn-secondary" onClick={() => navigate(-1)}>← Назад</button></div>;

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn-ghost" onClick={() => navigate('/admin/users')}>← Назад</button>
        <h1>{isEditMode ? 'Редактирование пользователя' : 'Одобрение регистрации'}</h1>
      </div>

      <div className="approval-layout">
        <div className="info-card user-info-card">
          <h3>Данные пользователя</h3>
          <div className="info-row"><span>Имя</span><span>{user.full_name}</span></div>
          <div className="info-row"><span>Telegram</span><span>{user.telegram_username ? `@${user.telegram_username}` : `ID: ${user.telegram_id}`}</span></div>
          <div className="info-row"><span>Города (выбрал)</span><span>{(user.cities_requested || []).join(', ') || '—'}</span></div>
          <div className="info-row description-row"><span>Описание</span><p>{user.self_description || '—'}</p></div>
          <div className="info-row"><span>Статус</span><span>{user.status === 'approved' ? '✅ Одобрен' : user.status === 'rejected' ? '❌ Отклонён' : '⏳ Ожидает'}</span></div>
          <div className="info-row"><span>Дата заявки</span><span>{new Date(user.created_at).toLocaleString('ru-RU')}</span></div>
        </div>

        <div className="approval-form">
          {error && <div className="error-msg">{error}</div>}

          <div className="form-group">
            <label>Город *</label>
            <div className="radio-group">
              {CITIES.map(c => (
                <label key={c} className={`radio-chip ${city === c ? 'selected' : ''}`}>
                  <input type="radio" name="city" value={c} checked={city === c} onChange={() => setCity(c)} />
                  {c}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Роль *</label>
            <div className="roles-group">
              <div className="roles-section">
                <div className="roles-section-label">Розничные</div>
                {ROLES.filter(r => r.group === 'retail').map(r => (
                  <label key={r.value} className={`radio-chip ${role === r.value ? 'selected' : ''}`}>
                    <input type="radio" name="role" value={r.value} checked={role === r.value} onChange={() => { setRole(r.value); setNetwork(''); setPoint(''); setPlace(''); }} />
                    {r.label}
                  </label>
                ))}
              </div>
              <div className="roles-section">
                <div className="roles-section-label">Прочие</div>
                {ROLES.filter(r => r.group === 'other').map(r => (
                  <label key={r.value} className={`radio-chip ${role === r.value ? 'selected' : ''}`}>
                    <input type="radio" name="role" value={r.value} checked={role === r.value} onChange={() => { setRole(r.value); setNetwork(''); setPoint(''); setPlace(''); }} />
                    {r.label}
                  </label>
                ))}
              </div>
              <div className="roles-section">
                {ROLES.filter(r => r.group === 'head').map(r => (
                  <label key={r.value} className={`radio-chip radio-chip--special ${role === r.value ? 'selected' : ''}`}>
                    <input type="radio" name="role" value={r.value} checked={role === r.value} onChange={() => { setRole(r.value); setNetwork(''); setPoint(''); setPlace(''); }} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {isAutoAll && (
            <div className="info-card success-card">
              {role === 'company_head' && '✅ Руководитель компании — доступ ко всем сетям, Офису и Складу'}
              {role === 'retail_manager' && '✅ Руководитель розницы — доступ к Hookah-Service, Tabakov-Shop и Офису'}
              {role === 'marketer' && '✅ Маркетолог — доступ к Hookah-Service, Tabakov-Shop и Офису'}
            </div>
          )}
          {isFixedNetwork && (
            <div className="info-card success-card">
              {role === 'admin_retail_hs' && '✅ Администратор розницы HS — доступ к магазинам Hookah-Service и Офису'}
              {role === 'admin_retail_ts' && '✅ Администратор розницы TS — доступ к магазинам Tabakov-Shop (Магнитогорск) и Офису'}
            </div>
          )}

          {(isNetworkOnly || isNetworkAndPoint) && (
            <div className="form-group">
              <label>Сеть *{isNetworkOnly && <span className="label-hint"> — все точки сети будут доступны</span>}</label>
              <div className="radio-group">
                {Object.entries(networks).map(([key, net]) => (
                  <label key={key} className={`radio-chip ${network === key ? 'selected' : ''}`}>
                    <input type="radio" name="network" value={key} checked={network === key} onChange={() => { setNetwork(key); setPoint(''); }} />
                    {net.name}
                  </label>
                ))}
                {isNetworkAndPoint && (
                  <label className={`radio-chip ${network === 'all' ? 'selected' : ''}`}>
                    <input type="radio" name="network" value="all" checked={network === 'all'} onChange={() => { setNetwork('all'); setPoint(''); }} />
                    Все сети
                  </label>
                )}
              </div>
            </div>
          )}

          {isNetworkAndPoint && network && !isAllNetwork && points.length > 0 && (
            <div className="form-group">
              <label>Точка (основная) *</label>
              <select value={point} onChange={e => setPoint(e.target.value)}>
                <option value="">— Выбери точку —</option>
                {points.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}

          {isNetworkAndPoint && network && !isAllNetwork && points.length === 0 && (
            <div className="form-group">
              <label>Точка (основная) *</label>
              <input type="text" value={point} onChange={e => setPoint(e.target.value)} placeholder="Введите адрес точки" />
            </div>
          )}

          {isPlace && (
            <div className="form-group">
              <label>Место *</label>
              <div className="radio-group">
                {['Офис', 'Склад'].map(p => (
                  <label key={p} className={`radio-chip ${place === p ? 'selected' : ''}`}>
                    <input type="radio" name="place" value={p} checked={place === p} onChange={() => setPlace(p)} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button className="btn-success" onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? 'Сохранение...' : isEditMode ? '💾 Сохранить' : '✅ Одобрить'}
            </button>
            {isEditMode && user.status !== 'rejected' && (
              <button className="btn-danger" onClick={handleReject} disabled={submitting}>Отклонить</button>
            )}
            <button className="btn-ghost" onClick={() => navigate('/admin/users')}>Отмена</button>
          </div>
        </div>
      </div>
    </div>
  );
}
